import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LazyAvatar } from '../components/LazyAvatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Users, Shield, Mail, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setUsers(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user and all their data? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#264653] font-outfit">All Users</h1>
          <p className="text-[#6B7280] mt-1">{users.length} registered users</p>
        </div>

        {/* Users List */}
        {users.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <Users className="w-16 h-16 text-[#08513F]/30 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Users Yet</h2>
            <p className="text-[#6B7280]">Users will appear here once they register.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {users.map((user, index) => (
              <Card 
                key={user.user_id}
                className="bg-white rounded-2xl border-[#D4AF37]/20 shadow-md hover:shadow-lg transition-shadow animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`user-card-${user.user_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <LazyAvatar
                      src={user.picture}
                      fallback={<span className="bg-[#08513F] text-white text-xl font-bold">{user.full_name?.charAt(0) || 'U'}</span>}
                      className="h-16 w-16 border-2 border-[#D4AF37]/30"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-[#264653] font-outfit">
                          {user.full_name}
                        </h3>
                        <Badge className={`rounded-full ${
                          user.role === 'admin' 
                            ? 'bg-[#D4AF37] text-white' 
                            : 'bg-[#08513F]/10 text-[#08513F]'
                        }`}>
                          <Shield className="w-3 h-3 mr-1" />
                          {user.role === 'admin' ? 'Admin' : 'Parent'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-[#6B7280]">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </span>
                      </div>
                    </div>

                    {user.role !== 'admin' && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeleteUser(user.user_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
                        data-testid={`delete-user-${user.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
